import { Contract, type ContractAmendmentSummary, Membership } from '@kalos/kalos-rpc';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { queryKeys } from '../../../hooks/react-query/constants';
import { ContractClientService, MembershipClientService } from '../../../tools/helpers';
import type { DerivedStatus } from '../types';
import { isClosedOut, isMembershipCurrent, mapMembershipStatus } from '../utils/mapContractStatus';
import { useContractAmendmentQuery } from './useContractAmendments';
import { useMembershipStatuses } from './useMembershipStatuses';

export interface PropertyMembership {
  membership: Membership;
  /** Never `'cancelled'`, `'renewed'` or `'expired'` — those rows are filtered out before ranking. */
  status: DerivedStatus;
  /** The open amendment the plan was added on; `status` is then the amendment's. */
  amendment?: ContractAmendmentSummary;
}

// Which membership speaks for the property when it has more than one live row. `unknown` is
// deliberately kept rather than dropped: mapMembershipStatus returns it when the contract embed is
// missing, and such a row still speaks for the property (painting the amber CTA). `cancelled`,
// `renewed` and `expired` are filtered out below and never reach this sort; they rank last to keep
// the map total.
const STATUS_RANK: Record<DerivedStatus, number> = {
  active: 0,
  scheduled_cancel: 1,
  pending_payment: 2,
  awaiting_confirmation: 3,
  not_active_yet: 4,
  admin_review: 5,
  unknown: 6,
  expired: 7,
  cancelled: 8,
  renewed: 9,
};

/**
 * The live (non-cancelled) memberships on one property, with the plan and contract embedded.
 * Exported as options so every caller asking "what's on this property" shares one cache entry —
 * the enrollment guard maps it over several properties, this module's hook classifies one.
 */
export const propertyMembershipQueryOptions = (propertyId: number) => ({
  queryKey: [queryKeys.memberships.root, 'propertyMembership', propertyId],
  queryFn: async (): Promise<Membership[]> => {
    // pbsql omits zero-value predicates, so a propertyId of 0 would read every membership in the
    // system unfiltered and unlimited — not an empty result.
    if (propertyId <= 0) return [];
    const filter = Membership.create();
    filter.propertyId = propertyId;
    filter.withoutLimit = true;
    filter.withType = true;
    filter.withContract = true;
    // Commercial plans carry their name and price on the instance lines, not the catalog type.
    filter.withLines = true;
    const res = await MembershipClientService.BatchGet(filter);
    return (res?.results ?? []).filter(isMembershipCurrent);
  },
});

/**
 * The property's current membership, if any. Cancelled rows are dropped, so a null result means the
 * property is free to enroll. One BatchGet with the embed flags covers what a summary surface needs:
 * plan name and cost, term dates, status, benefits and terms.
 */
export const usePropertyMembership = ({
  propertyId,
  enabled = true,
}: {
  propertyId: number;
  enabled?: boolean;
}) => {
  const isEnabled = enabled && propertyId > 0;

  const membershipsQuery = useQuery<Membership[]>({
    ...propertyMembershipQueryOptions(propertyId),
    enabled: isEnabled,
  });

  const statusesQuery = useMembershipStatuses({ enabled: isEnabled });
  const statusMap = statusesQuery.data;

  const top = useMemo<PropertyMembership | null>(() => {
    const rows = membershipsQuery.data ?? [];
    if (rows.length === 0) return null;
    const ranked = rows
      .map((membership) => ({
        membership,
        status: mapMembershipStatus(membership, membership.contract, statusMap).status,
      }))
      // Rows are already cancel-filtered; a closed-out classification here means the stored status
      // disagrees with the cancel fields, so it still shouldn't speak for the property.
      .filter((row) => !isClosedOut(row.status))
      .sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status]);
    return ranked[0] ?? null;
  }, [membershipsQuery.data, statusMap]);

  // A plan on an open amendment reads the amendment's review and payment state, not the contract's.
  const amendmentId = top?.membership.contractAmendmentId ?? 0;
  const amendmentQuery = useContractAmendmentQuery(amendmentId, isEnabled);
  const amendment = amendmentQuery.data;
  const data = useMemo<PropertyMembership | null>(() => {
    if (!top || !amendment || (!amendment.needsReview && !amendment.pendingPayment)) return top;
    return {
      ...top,
      status: amendment.pendingPayment ? 'pending_payment' : 'awaiting_confirmation',
      amendment,
    };
  }, [top, amendment]);

  return {
    data,
    // Both queries feed `status`: without the vocabulary it derives from the fallback path and then
    // changes when the map lands, so callers gating on this would paint the wrong state first.
    // The amendment read feeds it too, counted only when the plan has one: a disabled query stays
    // pending.
    isPending:
      membershipsQuery.isPending ||
      statusesQuery.isPending ||
      (amendmentId > 0 && amendmentQuery.isPending),
    isError: membershipsQuery.isError || (amendmentId > 0 && amendmentQuery.isError),
    /** The membership loaded but its amendment read failed. */
    amendmentFailed: !membershipsQuery.isError && amendmentId > 0 && amendmentQuery.isError,
    amendmentRetrying: amendmentQuery.isFetching,
    retryAmendment: () => void amendmentQuery.refetch(),
  };
};

export interface PropertyOccupancyResult {
  /** Property IDs with a live membership. `undefined` = occupancy unknown, not "none occupied". */
  data: Set<number> | undefined;
  /** Waiting on an answer. True whenever `data` is undefined and nothing errored. */
  isLoading: boolean;
  isError: boolean;
}

/**
 * Returns the set of property IDs already occupied by a membership — active or pending (awaiting
 * payment), excluding the closed-out ones (cancelled, renewed, expired) — so Step 1 can block
 * re-enrolling a property that has one.
 *
 * Shares `propertyMembershipQueryOptions` with `usePropertyMembership`, so a property another
 * surface already read is painted from cache rather than blocking on a fresh request.
 *
 * `data` is undefined until every property has answered: a present Set is definitive, and an empty
 * one means "none occupied" rather than "nothing has loaded".
 */
export const usePropertyActiveMemberships = ({
  propertyIds,
  enabled = true,
}: {
  propertyIds: number[];
  enabled?: boolean;
}): PropertyOccupancyResult => {
  const isEnabled = enabled && propertyIds.length > 0;

  const queries = useQueries({
    queries: propertyIds.map((propertyId) => ({
      ...propertyMembershipQueryOptions(propertyId),
      enabled: isEnabled && propertyId > 0,
    })),
  });

  const statusesQuery = useMembershipStatuses();
  const statusMap = statusesQuery.data;

  return useMemo(() => {
    // Keyed off the data itself, not the fetch flags: a disabled query reports isLoading false while
    // having no data, and reading that absence as "unoccupied" would silently unblock the guard. The
    // status vocabulary counts too, but only once there is a row to classify.
    const rowsPerProperty = queries.map((query) => query.data);
    const unresolved =
      rowsPerProperty.some((rows) => rows === undefined) ||
      (statusMap === undefined && rowsPerProperty.some((rows) => (rows ?? []).length > 0));
    const isError = queries.some((query) => query.isError) || statusesQuery.isError;
    return {
      data: unresolved
        ? undefined
        : new Set(
            propertyIds.filter((_, index) =>
              (rowsPerProperty[index] ?? []).some(
                (membership) =>
                  !isClosedOut(
                    mapMembershipStatus(membership, membership.contract, statusMap).status,
                  ),
              ),
            ),
          ),
      isLoading: unresolved && !isError,
      isError,
    };
  }, [queries, propertyIds, statusMap, statusesQuery.isError]);
};

/**
 * The same answer as `usePropertyActiveMemberships` for every property of one customer, in two
 * requests however many properties they have: the customer's contracts, then those contracts'
 * memberships. A plan on another customer's contract isn't seen.
 */
export const useCustomerOccupiedPropertyIds = ({
  customerId,
  enabled = true,
}: {
  customerId: number;
  enabled?: boolean;
}): PropertyOccupancyResult => {
  const contractsQuery = useQuery({
    queryKey: [queryKeys.contracts.root, 'customerContractIds', customerId],
    queryFn: async () => {
      const res = await ContractClientService.BatchGet(
        Contract.create({ userId: customerId, withoutLimit: true }),
      );
      // The wrapper returns undefined on failure; read as "no contracts" it would unblock the guard.
      if (!res) throw new Error("Couldn't load the customer's contracts");
      return res.results.map((contract) => contract.id);
    },
    enabled: enabled && customerId > 0,
  });
  const contractIds = contractsQuery.data;

  const membershipsQuery = useQuery({
    queryKey: [queryKeys.memberships.root, 'customerOccupancy', customerId, contractIds],
    queryFn: async () => {
      const res = await MembershipClientService.BatchGet(
        Membership.create({
          contractIdList: (contractIds ?? []).join(','),
          withoutLimit: true,
          withContract: true,
        }),
      );
      if (!res) throw new Error("Couldn't load the customer's memberships");
      return res.results.filter(isMembershipCurrent);
    },
    enabled: enabled && !!contractIds?.length,
  });

  const statusesQuery = useMembershipStatuses();
  const statusMap = statusesQuery.data;

  return useMemo(() => {
    const rows = contractIds?.length === 0 ? [] : membershipsQuery.data;
    const unresolved = rows === undefined || (statusMap === undefined && rows.length > 0);
    const isError = contractsQuery.isError || membershipsQuery.isError || statusesQuery.isError;
    return {
      data: unresolved
        ? undefined
        : new Set(
            rows
              .filter(
                (membership) =>
                  !isClosedOut(
                    mapMembershipStatus(membership, membership.contract, statusMap).status,
                  ),
              )
              .map((membership) => membership.propertyId),
          ),
      isLoading: unresolved && !isError,
      isError,
    };
  }, [
    contractIds,
    membershipsQuery.data,
    statusMap,
    contractsQuery.isError,
    membershipsQuery.isError,
    statusesQuery.isError,
  ]);
};
